import {
  fetchEventSource,
  EventStreamContentType,
  type EventSourceMessage,
} from '@microsoft/fetch-event-source';
import { getToken } from './keycloak';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export type SseKind = 'flow' | 'queue' | 'packages';

export interface SseHandlers {
  onMessage: (data: unknown) => void;
  onOpen?: () => void;
  onError?: (err: Error) => void;
}

export interface SseConnectionOptions extends SseHandlers {
  kind: SseKind;
  cluster?: string;
}

export interface OpsRunSseOptions extends SseHandlers {
  runId: string;
}

export interface SseConnection {
  close: () => void;
}

class FatalSseError extends Error {}

const MAX_CONSECUTIVE_ERRORS = 5;

function buildUrl(kind: SseKind, cluster?: string): string {
  const path = `/api/sse/${kind}`;
  const sp = new URLSearchParams();
  if (cluster) sp.set('cluster', cluster);
  const qs = sp.toString();
  return `${BASE}${path}${qs ? `?${qs}` : ''}`;
}

function connectToUrl(url: string, handlers: SseHandlers): SseConnection {
  const controller = new AbortController();
  let errorCount = 0;
  let closed = false;

  void (async (): Promise<void> => {
    try {
      const token = await getToken();
      await fetchEventSource(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: controller.signal,
        openWhenHidden: true,
        async onopen(response): Promise<void> {
          if (
            response.ok &&
            response.headers
              .get('content-type')
              ?.includes(EventStreamContentType)
          ) {
            errorCount = 0;
            handlers.onOpen?.();
            return;
          }
          throw new FatalSseError(`sse open failed: status ${response.status}`);
        },
        onmessage(ev: EventSourceMessage): void {
          if (!ev.data) return;
          try {
            const parsed = JSON.parse(ev.data) as unknown;
            handlers.onMessage(parsed);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('sse parse failed', err);
          }
        },
        onerror(err): number | null {
          if (closed) throw err;
          if (err instanceof FatalSseError) {
            handlers.onError?.(err);
            throw err;
          }
          errorCount += 1;
          if (errorCount >= MAX_CONSECUTIVE_ERRORS) {
            const fatal = new FatalSseError(
              `sse giving up after ${errorCount} errors`
            );
            handlers.onError?.(fatal);
            throw fatal;
          }
          return null;
        },
        onclose(): void {
          if (closed) return;
          throw new Error('sse closed; will retry');
        },
      });
    } catch (err) {
      if (!closed) handlers.onError?.(err as Error);
    }
  })();

  return {
    close(): void {
      closed = true;
      controller.abort();
    },
  };
}

export function createSseConnection(opts: SseConnectionOptions): SseConnection {
  return connectToUrl(buildUrl(opts.kind, opts.cluster), opts);
}

export function createOpsRunSseConnection(
  opts: OpsRunSseOptions
): SseConnection {
  return connectToUrl(`${BASE}/api/ops/runs/${opts.runId}/events`, opts);
}
